<script setup lang="ts">
  import {
    QuestionnairesService,
    OrganisationsService,
    type SuperUserQuestionnaire as Questionnaire,
    type SuperUserOrganisation as Organisation
  } from '@/types/generated'
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse
  } from '@/components/admin/PaginatedTable.vue'
  import { ROUTES } from '@/router/routes'
  import UniqueTableDataCell from '@/components/admin/table/UniqueTableDataCell.vue'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { TableRow, TableCell } from '@/components/ui/table'
  import { useRouter } from 'vue-router'
  import QuestionnaireEnabledCustomerDisplay from '@/components/admin/questionnaire/QuestionnaireEnabledCustomerDisplay.vue'
  import { onMounted, ref } from 'vue'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import { genericErrorToast } from '@/lib/toasts'

  const router = useRouter()
  const organisations = ref<Organisation[]>([])
  const showDeleteModal = ref(false)
  const questionnaireToDelete = ref<Questionnaire | null>(null)
  const paginatedTableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)

  const fetchQuestionnaires = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<Questionnaire>> => {
    return QuestionnairesService.questionnairesList({
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search
    }) as unknown as PaginatedResponse<unknown>
  }

  const createQuestionnaire = async () => {
    const newQuestionnaire = await QuestionnairesService.questionnairesCreate({
      requestBody: {
        title: 'New Questionnaire',
        enabled_custom_orgs: [],
        enabled_all_orgs: false,
        schema: []
      }
    })
    router.push(
      ROUTES.ADMIN.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(':uuid', newQuestionnaire.uuid)
    )
  }

  const openDeleteModal = (questionnaire: Questionnaire) => {
    questionnaireToDelete.value = questionnaire
    showDeleteModal.value = true
  }

  const closeDeleteModal = () => {
    showDeleteModal.value = false
    questionnaireToDelete.value = null
  }

  const confirmDelete = async () => {
    if (questionnaireToDelete.value) {
      try {
        await QuestionnairesService.questionnairesDestroy({
          uuid: questionnaireToDelete.value.uuid
        })
        closeDeleteModal()
        // Refresh the questionnaire list
        if (paginatedTableRef.value) {
          await paginatedTableRef.value.refreshData()
        }
      } catch (error) {
        genericErrorToast()
      }
    }
  }

  onMounted(async () => {
    organisations.value = (await OrganisationsService.organisationsList({ limit: 0 }))
      .results as Organisation[]
  })
</script>

<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Questionnaires', to: ROUTES.ADMIN.QUESTIONNAIRES.QUESTIONNAIRE_LIST }
          ]"
        />
        <div class="flex items-center justify-end gap-x-2">
          <button
            class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
              text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
              focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
            @click="createQuestionnaire"
          >
            <svg
              class="hidden size-3 flex-shrink-0 sm:block"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M8 1C8.55228 1 9 1.44772 9 2V7L14 7C14.5523 7 15 7.44771 15 8C15 8.55228 14.5523 9 14 9L9 9V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V9.00001L2 9.00001C1.44772 9.00001 1 8.5523 1 8.00001C0.999999 7.44773 1.44771 7.00001 2 7.00001L7 7.00001V2C7 1.44772 7.44772 1 8 1Z"
              ></path>
            </svg>
            Create questionnaire
          </button>
        </div>
      </div>
      <!-- End Page Header -->

      <!-- Users Table Card -->
      <div class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div>
          <!-- Tab Content -->
          <div class="">
            <!-- Table Section -->
            <div
              class="[&amp;::-webkit-scrollbar]:h-2 [&amp;::-webkit-scrollbar-thumb]:rounded-full
                [&amp;::-webkit-scrollbar-track]:bg-gray-100 [&amp;::-webkit-scrollbar-thumb]:bg-gray-300
                &amp;::-webkit-scrollbar-track]:bg-neutral-700 &amp;::-webkit-scrollbar-thumb]:bg-neutral-500
                overflow-x-auto"
            >
              <div class="inline-block min-w-full align-middle">
                <!-- Table -->
                <PaginatedTableView
                  ref="paginatedTableRef"
                  :fetchData="fetchQuestionnaires"
                  searchableFields="UUID, title, description, customer, category, law field, law topic"
                >
                  <template #table-head>
                    <TableCell>UUID</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell class="min-w-[300px]">Customers</TableCell>
                    <TableCell>Law field</TableCell>
                    <TableCell>Law topic</TableCell>
                    <TableCell></TableCell>
                  </template>
                  <template #table-row="{ items: questionnaires }: { items: Questionnaire[] }">
                    <TableRow
                      v-for="questionnaire in questionnaires"
                      :key="questionnaire.uuid"
                    >
                      <UniqueTableDataCell :truncate="true">
                        <router-link
                          :to="
                            ROUTES.ADMIN.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(
                              ':uuid',
                              questionnaire.uuid
                            )
                          "
                        >
                          {{ questionnaire.uuid }}
                        </router-link>
                      </UniqueTableDataCell>
                      <TableCell>
                        {{ questionnaire.title }}
                      </TableCell>
                      <TableCell>
                        {{ questionnaire.description }}
                      </TableCell>
                      <TableCell>
                        <QuestionnaireEnabledCustomerDisplay
                          :questionnaire="questionnaire"
                          :organisations="organisations"
                        />
                      </TableCell>
                      <TableCell>
                        {{ questionnaire.law_field }}
                      </TableCell>
                      <TableCell>
                        {{ questionnaire.law_topic }}
                      </TableCell>
                      <TableCell>
                        <TableOptionsButton
                          :options="[
                            {
                              label: 'Edit',
                              to: ROUTES.ADMIN.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(
                                ':uuid',
                                questionnaire.uuid
                              )
                            },
                            {
                              label: 'Duplicate',
                              onClick: async () => {
                                const copy = await QuestionnairesService.questionnairesCopyCreate({
                                  uuid: questionnaire.uuid
                                })
                                if (copy) {
                                  router.push(
                                    ROUTES.ADMIN.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(
                                      ':uuid',
                                      copy.uuid
                                    )
                                  )
                                }
                              }
                            },
                            {
                              label: 'Delete',
                              onClick: () => openDeleteModal(questionnaire)
                            }
                          ]"
                        />
                      </TableCell>
                    </TableRow>
                  </template>
                </PaginatedTableView>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <Dialog
      :open="showDeleteModal"
      @update:open="closeDeleteModal"
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the questionnaire "{{ questionnaireToDelete?.title }}"?
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            @click="closeDeleteModal"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            @click="confirmDelete"
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>

<style scoped></style>
